/**
 * ChartsTab Component
 *
 * Combines all chart components for the EventDetail page.
 * Displays sportsbook odds, Polymarket prices, price gap, and signal strength charts.
 */

import SportsbookOddsChart from '../components/SportsbookOddsChart';
import PolymarketChart from '../components/PolymarketChart';
import PriceGapChart from '../components/PriceGapChart';
import SignalStrengthChart from '../components/SignalStrengthChart';
import type { EventDetail, Signal } from '../../../types';
import type {
  LineChartDataPoint,
  LiveOdds,
  PolymarketChartData,
  PriceGapChartData,
  SignalChartData,
} from '../utils/chartProcessors';

interface ChartsTabProps {
  event: EventDetail['event'];
  lineChartData: LineChartDataPoint[];
  liveOdds: LiveOdds[];
  bookmakersInData: string[];
  polymarketChartData: PolymarketChartData[];
  priceGapChartData: PriceGapChartData[];
  signalChartData: SignalChartData[];
  selectedBookmakers: Set<string>;
  toggleBookmaker: (bookmaker: string) => void;
  sharpBooks: string[];
  squareBooks: string[];
  lastOddsUpdate: string | null;
  signals?: Signal[];
}

export default function ChartsTab({
  event,
  lineChartData,
  liveOdds,
  bookmakersInData,
  polymarketChartData,
  priceGapChartData,
  signalChartData,
  selectedBookmakers,
  toggleBookmaker,
  sharpBooks,
  squareBooks,
  lastOddsUpdate,
  signals = [],
}: ChartsTabProps) {
  const hasOddsData = lineChartData.length > 0;
  const hasPolymarketData = polymarketChartData.length > 0;
  const hasPriceGapData = priceGapChartData.length > 0;
  const hasSignalData = signalChartData.length > 0;

  if (!hasOddsData && !hasPolymarketData && !hasPriceGapData && !hasSignalData) {
    return (
      <div className="card text-center py-12">
        <p className="text-gray-400">No chart data available</p>
        <p className="text-gray-500 text-sm mt-2">
          Historical data will appear as odds and prices are collected over time
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Sportsbook Odds Chart */}
      {hasOddsData && (
        <SportsbookOddsChart
          event={event}
          lineChartData={lineChartData}
          liveOdds={liveOdds}
          bookmakersInData={bookmakersInData}
          selectedBookmakers={selectedBookmakers}
          toggleBookmaker={toggleBookmaker}
          sharpBooks={sharpBooks}
          squareBooks={squareBooks}
          lastOddsUpdate={lastOddsUpdate}
        />
      )}

      {/* Polymarket Price Chart */}
      {hasPolymarketData && (
        <PolymarketChart
          event={event}
          polymarketChartData={polymarketChartData}
        />
      )}

      {/* Price Gap Chart */}
      {hasPriceGapData && (
        <PriceGapChart priceGapChartData={priceGapChartData} />
      )}

      {/* Signal Strength Chart */}
      {hasSignalData && (
        <SignalStrengthChart
          signalChartData={signalChartData}
          signals={signals}
        />
      )}
    </div>
  );
}
